// Argus — DesktopWindow: Capture (screenshot snapshot) + Stream (live screen) toggle.
// Capture: emit screenshot + poll → display image cu auto-refresh interval dropdown.
// Stream: emit stream_start screen + LiveScreenPlayer + emit stream_stop pe close/toggle.
// Multi-monitor selector deferred Faza 5 iter 2 (cere ScreenshotService.Capture(monitorIndex)).

import { useEffect, useRef, useState } from 'react';
import {
  Box, ToggleButton, ToggleButtonGroup, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Typography, IconButton, Tooltip,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import { emitStreamStart, emitStreamStop, VIDEO_CONFIG_PRESETS, QUALITY_BITRATES, QUALITY_LABELS, type StreamQuality } from '../lib/streams';
import { wsClient } from '../lib/wsClient';
import { browserRecorder } from '../lib/browserRecorder';
import { fetchAgents } from '../lib/api';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../lib/auth';
import { useAutoActionsStore } from '../store/autoActionsStore';
import LiveScreenPlayer, { type LivePlayerHandle } from '../components/LiveScreenPlayer';
import { Lightbox } from '../components/Lightbox';
import { ArgusToolbar } from '../components/floating/ArgusToolbar';

type Mode = 'capture' | 'stream';
type AutoRefresh = 'off' | '5s' | '10s' | '30s' | '1min';

const AUTO_REFRESH_MS: Record<AutoRefresh, number> = {
  off: 0, '5s': 5000, '10s': 10000, '30s': 30000, '1min': 60000,
};

export interface DesktopWindowProps {
  agentId: string;
  agentOnline: boolean;
}

export function DesktopWindow({ agentId, agentOnline }: DesktopWindowProps): JSX.Element {
  // Auto-trigger screenshot pe mount + setează auto-refresh '5s' implicit pentru
  // experiență "captură real-time" la deschidere modal (UX 2026-05-10).
  // Consume one-shot autoAction din sidebar Quick Screenshot — pattern păstrat dar
  // comportament default = same flow (screenshot + auto-refresh).
  const autoActionConsumedRef = useRef(false);
  useEffect(() => {
    if (autoActionConsumedRef.current) return;
    autoActionConsumedRef.current = true;
    useAutoActionsStore.getState().consume(`${agentId}:desktop`);
    // Defer — wait pentru `agentOnline` true + WS connected. Polling 200ms x 25 (5s timeout).
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts++;
      if (agentOnline) {
        clearInterval(timer);
        void captureScreenshot();
      } else if (attempts > 25) {
        clearInterval(timer);
      }
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const [mode, setMode] = useState<Mode>('capture');
  const [autoRefresh, setAutoRefresh] = useState<AutoRefresh>('off');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamId, setStreamId] = useState<string | null>(null);
  const [streamStarting, setStreamStarting] = useState(false);
  // Bloc recording-browser-pivot 2026-05-09: recording = browser-side via MediaRecorder.
  const [recordingActive, setRecordingActive] = useState<boolean>(false);
  const playerRef = useRef<LivePlayerHandle>(null);
  const { data: agents } = useQuery({ queryKey: ['agents'], queryFn: fetchAgents, refetchInterval: 30_000 });
  const agentMeta = agents?.find((a) => a.id === agentId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [quality, setQuality] = useState<StreamQuality>(() => {
    try { return (localStorage.getItem(`argus.desktop.${agentId}.quality`) as StreamQuality) || 'standard'; }
    catch { return 'standard'; }
  });
  useEffect(() => {
    try { localStorage.setItem(`argus.desktop.${agentId}.quality`, quality); } catch {}
  }, [agentId, quality]);
  const intervalRef = useRef<number | null>(null);

  // Bloc ws-direct-batch-2 2026-05-05: WS direct screenshot — base64 inline din response,
  // afișat ca data: URL. Drop disk artifact + poll status REST flow.
  const previousBlobUrlRef = useRef<string | null>(null);
  const captureScreenshot = async (): Promise<void> => {
    if (!agentOnline || capturing) return;
    setCapturing(true);
    setError(null);
    try {
      const result = await wsClient.requestScreenshot(agentId, '');
      // Decode base64 → Blob → URL pentru consistency cu existing img tag (revoke la următorul snapshot).
      const binStr = atob(result.png);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      // Revoke previous URL pentru a evita leak memory la auto-refresh.
      if (previousBlobUrlRef.current !== null) {
        URL.revokeObjectURL(previousBlobUrlRef.current);
      }
      previousBlobUrlRef.current = url;
      setImageUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Screenshot error');
    } finally {
      setCapturing(false);
    }
  };

  const startStream = async (): Promise<void> => {
    if (!agentOnline || streamStarting || streamId) return;
    setStreamStarting(true);
    setError(null);
    try {
      const baseConfig = VIDEO_CONFIG_PRESETS.medium!;
      const config = { ...baseConfig, bitrate: QUALITY_BITRATES[quality] };
      const result = await emitStreamStart(agentId, '', config, { sources: ['screen'], quality });
      setStreamId(result.streamId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start stream');
    } finally {
      setStreamStarting(false);
    }
  };

  const stopStream = async (): Promise<void> => {
    if (!streamId) return;
    // Cascade: stop recording first ca download să fire înainte ca tracks să cadă.
    if (recordingActive) {
      browserRecorder.stopForAgent(agentId, 'desktop');
      setRecordingActive(false);
    }
    const id = streamId;
    setStreamId(null);
    try {
      await emitStreamStop(agentId, id);
    } catch (err) {
      console.warn('Stop stream failed', err);
    }
  };

  const startRecording = (): void => {
    if (!streamId || recordingActive) return;
    setError(null);
    try {
      const mediaStream = playerRef.current?.captureStream();
      if (!mediaStream) {
        throw new Error('Stream player not ready (no MediaStream available).');
      }
      browserRecorder.start({
        agentId,
        pcName: agentMeta?.pcName ?? agentMeta?.hostname ?? 'unknown',
        windowsUsername: agentMeta?.windowsUsername ?? agentMeta?.lastSeenUser ?? 'system',
        source: 'desktop',
        mediaStream,
        bitrate: QUALITY_BITRATES[quality],
      });
      setRecordingActive(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');
    }
  };

  const stopRecording = (): void => {
    if (!recordingActive) return;
    browserRecorder.stopForAgent(agentId, 'desktop');
    setRecordingActive(false);
  };

  // Auto-refresh interval pentru Capture mode.
  useEffect(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (mode !== 'capture' || autoRefresh === 'off' || !agentOnline) return;
    const ms = AUTO_REFRESH_MS[autoRefresh];
    intervalRef.current = window.setInterval(() => { void captureScreenshot(); }, ms);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, autoRefresh, agentOnline, agentId]);

  // Stop stream când component unmount (modal close) sau pagină se reîncarcă (refresh/close tab).
  // Folosim ref ca să citim ID-ul curent ([] deps capturează STALE streamId din primul render).
  const streamIdRef = useRef<string | null>(null);
  useEffect(() => { streamIdRef.current = streamId; }, [streamId]);
  useEffect(() => {
    const stopIfActive = (useKeepalive: boolean) => {
      const id = streamIdRef.current;
      if (!id) return;
      streamIdRef.current = null;
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://argus.local:8080';
      const csrfToken = useAuthStore.getState().csrfToken ?? '';
      const body = JSON.stringify({
        command: 'stream_stop',
        justification: useKeepalive ? 'page unload' : 'modal close',
        params: { streamId: id },
      });
      // keepalive: true allows the request to outlive the page during refresh/close.
      fetch(`${baseUrl}/api/v1/agents/${agentId}/commands`, {
        method: 'POST',
        credentials: 'include',
        keepalive: useKeepalive,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body,
      }).catch(() => {});
    };
    const beforeUnload = () => stopIfActive(true);
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      stopIfActive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup recording la unmount panel — declanșează download .webm dacă recording era activ.
  useEffect(() => {
    return () => {
      browserRecorder.stopForAgent(agentId, 'desktop');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // beforeunload guard: dacă stream/record activ, browser cere confirmare la close/refresh.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (streamId || recordingActive) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [streamId, recordingActive]);

  const handleModeChange = (_: React.MouseEvent, value: Mode | null) => {
    if (value === null || value === mode) return;
    if (mode === 'stream' && streamId) {
      void stopStream();
    }
    setMode(value);
    setError(null);
  };

  return (
    <Box data-role="desktop-panel" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ArgusToolbar>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={handleModeChange} sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1 } }}>
          <ToggleButton value="capture"><PhotoCameraIcon fontSize="small" sx={{ mr: 0.5 }} />Capture</ToggleButton>
          <ToggleButton value="stream"><VideocamIcon fontSize="small" sx={{ mr: 0.5 }} />Stream</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'capture' ? (
          <>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Auto-refresh</InputLabel>
              <Select
                label="Auto-refresh"
                value={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.value as AutoRefresh)}
                disabled={!agentOnline}
              >
                <MenuItem value="off">Off</MenuItem>
                <MenuItem value="5s">5 seconds</MenuItem>
                <MenuItem value="10s">10 seconds</MenuItem>
                <MenuItem value="30s">30 seconds</MenuItem>
                <MenuItem value="1min">1 minute</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Snapshot">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!agentOnline || capturing}
                  onClick={() => void captureScreenshot()}
                >
                  {capturing ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Quality</InputLabel>
              <Select
                label="Quality"
                value={quality}
                onChange={(e) => {
                  const newQ = e.target.value as StreamQuality;
                  setQuality(newQ);
                  if (streamId) {
                    void stopStream().then(async () => {
                      await new Promise((r) => setTimeout(r, 300));
                      const baseConfig = VIDEO_CONFIG_PRESETS.medium!;
                      const config = { ...baseConfig, bitrate: QUALITY_BITRATES[newQ] };
                      try {
                        const result = await emitStreamStart(agentId, '', config, { sources: ['screen'], quality: newQ });
                        setStreamId(result.streamId);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Failed to restart stream');
                      }
                    });
                  }
                }}
              >
                {(Object.keys(QUALITY_LABELS) as StreamQuality[]).map((q) => (
                  <MenuItem key={q} value={q}>{QUALITY_LABELS[q]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={streamId ? 'Stop stream' : 'Start stream'}>
              <span>
                <IconButton
                  size="small"
                  color={streamId ? 'error' : 'primary'}
                  disabled={!agentOnline || streamStarting}
                  onClick={() => void (streamId ? stopStream() : startStream())}
                >
                  {streamStarting ? <CircularProgress size={18} /> : streamId ? <VideocamOffIcon /> : <VideocamIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={recordingActive ? 'Stop recording (.webm download)' : (streamId ? 'Start recording (browser-side .webm)' : 'Start streaming first to enable recording')}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!agentOnline || !streamId}
                  onClick={() => (recordingActive ? stopRecording() : startRecording())}
                >
                  {recordingActive ? <StopIcon /> : <FiberManualRecordIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </ArgusToolbar>

      {!agentOnline ? (
        <Alert severity="warning" variant="filled" sx={{ borderRadius: 0, py: 0.5 }}>Agent offline</Alert>
      ) : null}
      {error ? (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 0, py: 0.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Box data-role="desktop-content" sx={{ flexGrow: 1, overflow: 'auto', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover' }}>
        {mode === 'capture' ? (
          imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Screenshot"
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
              onClick={() => setLightboxOpen(true)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Press "Snapshot" to capture a new screenshot.
            </Typography>
          )
        ) : streamId ? (
          <LiveScreenPlayer ref={playerRef} streamId={streamId} onTerminated={(reason) => { setStreamId(null); setError(`Stream terminated: ${reason}`); }} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Press "Start stream" for live screen.
          </Typography>
        )}
      </Box>

      {/* Status bar — afișează modul + calitatea curentă. */}
      <Box data-role="desktop-statusbar" sx={{ borderTop: 1, borderColor: 'divider', backgroundColor: 'background.paper', px: 1, py: 0.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', minHeight: 24 }}>
        <Typography variant="caption" color="text.secondary"><strong>Mode:</strong> {mode === 'capture' ? 'Capture' : 'Stream'}</Typography>
        {mode === 'stream' && <Typography variant="caption" color="text.secondary"><strong>Quality:</strong> {QUALITY_LABELS[quality]}</Typography>}
        {mode === 'capture' && <Typography variant="caption" color="text.secondary"><strong>Auto-refresh:</strong> {autoRefresh}</Typography>}
        {recordingActive && (
          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
            ● REC (browser .webm)
          </Typography>
        )}
      </Box>

      <Lightbox open={lightboxOpen} onClose={() => setLightboxOpen(false)} src={imageUrl} alt="Screenshot full" />
    </Box>
  );
}
